import axios, { AxiosError, AxiosInstance, AxiosResponse } from "axios";
import axiosRetry from "axios-retry";
import { useEffect } from "react";
import { ApiHeaders } from "../api/config/apiHeaders.constants";
import { useSelector } from "react-redux";
import { RootStateType } from "../redux/features/rootSlice";
import { triggerOnUnauthorized } from "./authEvent";

// app.at / app.rt are HttpOnly cookies. They are attached by the browser
// because every request is sent with credentials; no Authorization header.
export const baseInstance: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_PROXY_BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    ...ApiHeaders.getAuthHeader(), // Include any additional headers from ApiHeaders
  },
});
export const userAddressBaseInstance: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_PROXY_BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

const errorStatusCode = [500, 502, 503, 504, 403, 401];

const customRetryDelay = (retryCount: number) =>
  Math.min(1000 * Math.pow(2, retryCount - 1), 3000);

// Every call goes through the proxy as an HTTP POST; the real method is in the
// body. Only proxied GETs are safe to repeat on 5xx/403/network errors, so
// writes (send OTP, add address, change password...) are never duplicated.
// A 401 is always retried once, after a refresh, because the upstream call
// was rejected before it ran.
const getProxiedMethod = (config: any): string => {
  try {
    const data =
      typeof config?.data === "string" ? JSON.parse(config.data) : config?.data;
    return String(data?.method || config?.method || "").toUpperCase();
  } catch {
    return "";
  }
};

const applyRetryLogic = (apiInstance: AxiosInstance) => {
  axiosRetry(apiInstance, {
    retries: 3,
    // No backoff on 401: the refresh already happened, retry immediately.
    retryDelay: (retryCount, error: any) =>
      error?.response?.status === 401 ? 0 : customRetryDelay(retryCount),
    retryCondition: async (error) => {
      // A custom retryCondition bypasses axios-retry's built-in cancel exclusion.
      if (error.code === "ERR_CANCELED" || error.code === "ECONNABORTED") return false;

      const status = error.response?.status ?? 500;
      if (!errorStatusCode.includes(status)) return false;

      if (status === 401) {
        // 401 gets one shot only: refresh once, retry once, then stop.
        const retryCount = (error.config as any)?.["axios-retry"]?.retryCount ?? 0;
        if (retryCount >= 1) return false;

        try {
          await triggerOnUnauthorized();
        } catch (refreshError) {
          console.error("Token refresh failed", refreshError);
          return false; // session is gone; the SDK is already redirecting
        }
        return true;
      }

      return getProxiedMethod(error.config) === "GET";
    },
    onRetry: (retryCount, error: any, requestConfig) => {
      console.error(
        `Request failed with ${error.response?.status ?? "Unknown Status code"}: ` +
          `${requestConfig.baseURL}${requestConfig.url}. Retrying attempt ${retryCount}...`
      );
    },
  });
};

// Registered at module load so axios-retry's response interceptor runs before
// the error normaliser registered in AxiosInterceptor below.
applyRetryLogic(baseInstance);
applyRetryLogic(userAddressBaseInstance);

function AxiosInterceptor({ children }: { children: JSX.Element }) {
  const storedClientId = useSelector((state: RootStateType) => state.configStateReducer.clientId);
  useEffect(() => {
    function addClientId(request: any) {
      try {
        const existing = request.data
          ? typeof request.data === "string"
            ? JSON.parse(request.data)
            : request.data
          : {};
        request.data = JSON.stringify({ client_id: storedClientId, ...existing });
      } catch (error) {
        console.error(error);
      }
      return request;
    }
    const interceptorBaseReq = baseInstance.interceptors.request.use(addClientId);
    const interceptorUserAddressReq =
      userAddressBaseInstance.interceptors.request.use(addClientId);

    // Converts axios errors into the { message, statuscode, error } shape the
    // sagas expect. Retries (and the 401 refresh) were already handled by
    // axios-retry. A retried request runs through this chain twice, so an
    // error that is already normalised is passed through untouched.
    function errorHandler(error: any) {
      if (!axios.isAxiosError(error)) {
        return Promise.reject(error);
      }
      const axiosError = error as AxiosError<any>;
      if (axiosError.response) {
        return Promise.reject(
          axiosError.response.data || {
            message: "Something Went Wrong",
            statuscode: axiosError.response.status || 500,
            error: true,
          }
        );
      }
      return Promise.reject(new Error("Something Went Wrong", { cause: 500 }));
    }
    function passThrough(response: AxiosResponse) {
      return response;
    }
    const interceptorBaseRes = baseInstance.interceptors.response.use(
      passThrough,
      errorHandler
    );
    const interceptorUserAddressRes =
      userAddressBaseInstance.interceptors.response.use(passThrough, errorHandler);
    return () => {
      baseInstance.interceptors.request.eject(interceptorBaseReq);
      userAddressBaseInstance.interceptors.request.eject(
        interceptorUserAddressReq
      );
      baseInstance.interceptors.response.eject(interceptorBaseRes);
      userAddressBaseInstance.interceptors.response.eject(
        interceptorUserAddressRes
      );
    };
  }, [storedClientId]);
  return children;
}
export { AxiosInterceptor };
